import { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { DataTable } from '../../components/ui/data-table';
import InlineFilter from '../../components/ui/inline-filter';
import StatsCard from '../../components/common/StatsCard';
import { DrawerForm } from '../../components/ui/drawer-form';
import DemoAccountDetailsDrawer from '../../components/accounts/DemoAccountDetailsDrawer';
import CreateDemoAccountForm from '../../components/accounts/CreateDemoAccountForm';
import { exportTableToExcel } from '../../utils';
import { DEMO_ACCOUNT_FILTER_FIELDS } from '../../constants/filters';
import {
  PlusCircle,
  TrendingUp,
  DollarSign,
  Eye,
  Settings,
  Download,
} from 'lucide-react';
import PageHeader from '../../components/common/PageHeader';
import LoadingSkeleton from '../../components/common/LoadingSkeleton';
import { DemoAccountData } from '../../components/accounts/CreateDemoAccountForm';
import { createDemoAccountColumns } from './columns/demo-account-columns';
import { MT5AccountUser } from '../../utils/types';
import { mt5API } from '../../services/api';
import EditLeverageModal from '../../components/accounts/EditLeverageModal';

const DemoAccounts = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [activeFilters, setActiveFilters] = useState<{ [key: string]: string }>(
    {}
  );
  const [showInlineFilters, setShowInlineFilters] = useState(false);
  const [showCreateAccountDrawer, setShowCreateAccountDrawer] = useState(false);
  const [isCreatingAccount, setIsCreatingAccount] = useState(false);

  const [selectedAccount, setSelectedAccount] = useState<MT5AccountUser | null>(
    null
  );
  const [showViewDrawer, setShowViewDrawer] = useState(false);
  const [loadingViewDetails, setLoadingViewDetails] = useState(false);
  const [selectedAccountForLeverage, setSelectedAccountForLeverage] = useState<{
    login: string | number;
    currentLeverage?: string | number;
    name?: string;
  } | null>(null);
  const [showEditLeverageModal, setShowEditLeverageModal] = useState(false);

  const filterFields = DEMO_ACCOUNT_FILTER_FIELDS;
  const [accounts, setAccounts] = useState<MT5AccountUser[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchAccounts();
  }, []);

  const fetchAccounts = async () => {
    setLoading(true);
    try {
      const response = await mt5API.getUsers();
      const data = Array.isArray(response)
        ? response
        : Array.isArray(response?.data)
          ? response.data
          : [];
      setAccounts(data);
    } catch (error) {
      console.error('Error fetching demo accounts from MT5 API:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleExportData = () => {
    exportTableToExcel(filteredAccounts, columns, 'demo-accounts');
  };

  const handleCreateDemoAccount = () => {
    setShowCreateAccountDrawer(true);
  };

  const handleDemoAccountSubmit = async (data: DemoAccountData) => {
    setIsCreatingAccount(true);
    try {
      const formatPassword = (pass?: string) => {
        if (!pass || !pass.trim()) return undefined;
        const p = pass.trim();
        return p.endsWith('!') ? p : p + '!';
      };

      const payload = {
        group: data.group,
        name: data.name,
        mainPassword: formatPassword(data.mainPassword),
        investPassword: formatPassword(data.investPassword),
        leverage: data.leverage,
      };
      await mt5API.createUser(payload);
      setShowCreateAccountDrawer(false);
      toast.success('Demo account created successfully!');
      fetchAccounts();
    } catch (error: any) {
      console.error('Error creating demo account:', error);
      toast.error(
        error?.response?.data?.message ||
          'Error creating demo account. Please try again.'
      );
    } finally {
      setIsCreatingAccount(false);
    }
  };

  const handleSearchChange = (value: string) => {
    setSearchTerm(value);
  };

  const handleSearchReset = () => {
    setSearchTerm('');
  };

  const handleFiltersApply = (filters: { [key: string]: string }) => {
    setActiveFilters(filters);
  };

  const handleFiltersClear = () => {
    setActiveFilters({});
  };

  const filteredAccounts = accounts.filter(account => {
    const loginStr = String(account.Login || (account as any).mt5Login || '');
    const nameStr = (account.Name || (account as any).name || '').toLowerCase();
    const groupStr = (
      account.Group ||
      (account as any).group ||
      ''
    ).toLowerCase();
    const emailStr = (
      account.Email ||
      (account as any).email ||
      ''
    ).toLowerCase();
    const countryStr = (
      account.Country ||
      (account as any).country ||
      ''
    ).toLowerCase();
    const statusStr = (
      account.Status ||
      (account as any).status ||
      'active'
    ).toLowerCase();
    const levStr = String(account.Leverage || (account as any).leverage || '');
    const balStr = String(account.Balance ?? (account as any).balance ?? 0);
    const credStr = String(account.Credit ?? (account as any).credit ?? 0);
    const sTerm = searchTerm.toLowerCase();

    const matchesSearch =
      searchTerm === '' ||
      loginStr.includes(sTerm) ||
      nameStr.includes(sTerm) ||
      groupStr.includes(sTerm) ||
      emailStr.includes(sTerm) ||
      countryStr.includes(sTerm) ||
      statusStr.includes(sTerm) ||
      levStr.includes(sTerm) ||
      balStr.includes(sTerm) ||
      credStr.includes(sTerm);

    const matchesStatus =
      !activeFilters.status || statusStr === activeFilters.status.toLowerCase();

    return matchesSearch && matchesStatus;
  });

  const handleViewDetails = async (login: number) => {
    const foundAccount = accounts.find(acc => acc.Login === login);
    setSelectedAccount(foundAccount || ({ Login: login } as MT5AccountUser));
    setShowViewDrawer(true);
    setLoadingViewDetails(true);
    try {
      const freshUserData = await mt5API.getUserByLogin(login);
      if (freshUserData) {
        setSelectedAccount(freshUserData);
      }
    } catch (error) {
      console.error('Error fetching demo account details:', error);
    } finally {
      setLoadingViewDetails(false);
    }
  };

  const handleRefreshAccount = (login: number) => {
    console.log('Refresh account:', login);
  };

  const columns = createDemoAccountColumns({
    onViewDetails: handleViewDetails,
    onRefreshAccount: handleRefreshAccount,
    onChangeLeverage: acc => {
      setSelectedAccountForLeverage({
        login: acc.Login ?? (acc as any).mt5Login ?? '',
        currentLeverage: acc.Leverage ?? (acc as any).leverage,
        name: acc.Name || (acc as any).name,
      });
      setShowEditLeverageModal(true);
    },
  });

  const totalBalance = accounts.reduce(
    (acc, curr) => acc + (curr.Balance || 0),
    0
  );
  const totalCredit = accounts.reduce(
    (acc, curr) => acc + (curr.Credit || 0),
    0
  );
  const activeCount = accounts.filter(
    a => (a.Status || 'active').toLowerCase() === 'active'
  ).length;

  return (
    <div className="relative">
      <div className="sticky top-0 z-20 bg-background pt-4">
        <PageHeader
          title="Demo Accounts"
          description="Get help with your account, trading, and platform issues"
          actionLabel="Create Demo Account"
          actionIcon={PlusCircle}
          onAction={handleCreateDemoAccount}
        />
      </div>

      <div className="py-4 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <StatsCard
          title="Total Balance"
          value={`$${totalBalance.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
          change="Across all demo accounts"
          icon={DollarSign}
        />

        <StatsCard
          title="Total Credit"
          value={`$${totalCredit.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
          change="Total credit bonus"
          icon={TrendingUp}
        />

        <StatsCard
          title="Active Accounts"
          value={activeCount.toString()}
          change={`${accounts.length - activeCount} inactive`}
          icon={Eye}
        />

        <StatsCard
          title="Total Accounts"
          value={accounts.length.toString()}
          change="Demo account count"
          icon={Settings}
        />
      </div>

      <Card>
        <CardHeader>
          <div className="flex items-start justify-between gap-4 w-full">
            <div>
              <CardTitle>Demo Accounts</CardTitle>
            </div>
            <div className="flex space-x-2">
              {/* <Button
                variant="outline"
                size="sm"
                onClick={() => setShowInlineFilters(!showInlineFilters)}
              >
                <Filter className="h-4 w-4 mr-2" />
                {showInlineFilters ? 'Hide Filter' : 'Show Filter'}
              </Button> */}
              <Button variant="outline" size="sm" onClick={handleExportData}>
                <Download className="h-4 w-4 mr-2" />
                Export
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <InlineFilter
            showFilters={showInlineFilters}
            onToggle={() => setShowInlineFilters(!showInlineFilters)}
            filterFields={filterFields}
            activeFilters={activeFilters}
            onFilterChange={setActiveFilters}
            onFiltersClear={handleFiltersClear}
            onFiltersApply={handleFiltersApply}
            title="Filter Demo Accounts"
          />
          {loading ? (
            <LoadingSkeleton type="table" rows={5} cols={9} />
          ) : (
            <DataTable
              columns={columns}
              data={filteredAccounts}
              searchValue={searchTerm}
              searchPlaceholder="Search by Login, Name, Group, Email..."
              onSearchChange={handleSearchChange}
              onSearchReset={handleSearchReset}
              filterFields={filterFields}
              activeFilters={activeFilters}
              onFiltersChange={() => {}}
              showColumnVisibility={true}
              showAddButton={false}
              showExportButton={false}
              initialPageSize={10}
              initialSortBy={{ id: 'Login', desc: true }}
            />
          )}
        </CardContent>
      </Card>

      <DemoAccountDetailsDrawer
        isOpen={showViewDrawer}
        onClose={() => setShowViewDrawer(false)}
        account={selectedAccount}
        loading={loadingViewDetails}
      />

      <DrawerForm<DemoAccountData>
        open={showCreateAccountDrawer}
        onOpenChange={setShowCreateAccountDrawer}
        title="Create Demo Account"
        description="Open a new demo account to practice trading with virtual funds"
        onSubmit={handleDemoAccountSubmit}
        submitText="Create Account"
        loading={isCreatingAccount}
        width="lg"
      >
        <CreateDemoAccountForm onSubmit={handleDemoAccountSubmit} />
      </DrawerForm>

      <EditLeverageModal
        isOpen={showEditLeverageModal}
        onClose={() => {
          setShowEditLeverageModal(false);
          setSelectedAccountForLeverage(null);
        }}
        onSuccess={() => {
          fetchAccounts();
          setShowEditLeverageModal(false);
          setSelectedAccountForLeverage(null);
        }}
        account={selectedAccountForLeverage}
      />
    </div>
  );
};

export default DemoAccounts;
